import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { FilterOrigin } from "../shared";
import { Prefs } from "~/alias/prefs";
import ServerMessages from "~/servermessages";
import { reportAndLogError } from "~/utilities/background/logger";
async function isMigrationActive(experimentFlag) {
    const locales = await ewe.experiments.getFlag(experimentFlag);
    if (!Array.isArray(locales)) {
        return false;
    }
    const migrationDisabledFlag = await ewe.experiments.getFlag("allowlist-migration-disabled");
    if (migrationDisabledFlag) {
        return false;
    }
    const userLocale = browser.i18n.getUILanguage();
    return !locales.includes(userLocale);
}
async function migratePopupAllowlistsToSmartAllowlisting() {
    if (!(await isMigrationActive("allowlist-migration-disabled-locale"))) {
        return false;
    }
    const origins = [FilterOrigin.popup, FilterOrigin.wizard];
    const minDate = new Date(2020, 10, 11);
    const maxDate = new Date(2023, 11, 1);
    const filters = await ewe.filters.getUserFilters();
    const documentAllowLists = filters.filter((filter) => filter.type === "allowing" && filter.text.includes("$document"));
    const documentAllowListsWithMetadata = await Promise.all(documentAllowLists.map(async (filter) => {
        const metadata = await ewe.filters.getMetadata(filter.text);
        return { ...filter, metadata };
    }));
    const nonSmartAllowlists = documentAllowListsWithMetadata.filter(({ metadata }) => !(metadata === null || metadata === void 0 ? void 0 : metadata.autoExtendMs) && !(metadata === null || metadata === void 0 ? void 0 : metadata.expiresAt));
    const allowlistsFromGfc = nonSmartAllowlists.filter(({ metadata }) => (metadata === null || metadata === void 0 ? void 0 : metadata.origin) === FilterOrigin.web &&
        (metadata === null || metadata === void 0 ? void 0 : metadata.created) &&
        metadata.created >= minDate.getTime() &&
        metadata.created <= maxDate.getTime());
    const allowlistsToTransition = nonSmartAllowlists.filter(({ metadata }) => !(metadata === null || metadata === void 0 ? void 0 : metadata.autoExtendMs) && !(metadata === null || metadata === void 0 ? void 0 : metadata.expiresAt) && origins.includes(metadata === null || metadata === void 0 ? void 0 : metadata.origin));
    const autoExtendMs = Prefs.get("allowlisting_auto_extend_ms");
    await Promise.all(allowlistsToTransition.map(async ({ text: filterText, metadata }) => {
        await ewe.filters.setMetadata(filterText, {
            ...metadata,
            autoExtendMs,
            expiresAt: Date.now() + autoExtendMs,
        });
    }));
    ServerMessages.recordGeneralMessage("migrated_popup_allowlists", null, {
        transitionedCount: allowlistsToTransition.length,
        fromGfcCount: allowlistsFromGfc.length,
        allowlistsLeftCount: nonSmartAllowlists.length - allowlistsToTransition.length - allowlistsFromGfc.length,
    });
    return true;
}
async function migrateAllAllowlistsToSmartAllowlisting() {
    if (!(await isMigrationActive("all-allowlists-migration-disabled-locale"))) {
        return false;
    }
    const filters = await ewe.filters.getUserFilters();
    const documentAllowLists = filters.filter((filter) => filter.type === "allowing" && filter.text.includes("$document"));
    const documentAllowListsWithMetadata = await Promise.all(documentAllowLists.map(async (filter) => {
        const metadata = await ewe.filters.getMetadata(filter.text);
        return { ...filter, metadata };
    }));
    const allowlistsFromWeb = documentAllowListsWithMetadata.filter(({ metadata }) => (metadata === null || metadata === void 0 ? void 0 : metadata.origin) === FilterOrigin.web);
    const allowlistsToTransition = documentAllowListsWithMetadata.filter(({ metadata }) => !(metadata === null || metadata === void 0 ? void 0 : metadata.autoExtendMs) && !(metadata === null || metadata === void 0 ? void 0 : metadata.expiresAt) && !(metadata === null || metadata === void 0 ? void 0 : metadata.origin));
    const autoExtendMs = Prefs.get("allowlisting_auto_extend_ms");
    await Promise.all(allowlistsToTransition.map(async ({ text: filterText, metadata }) => {
        await ewe.filters.setMetadata(filterText, {
            ...metadata,
            autoExtendMs,
            expiresAt: Date.now() + autoExtendMs,
        });
    }));
    ServerMessages.recordGeneralMessage("migrated_all_allowlists", null, {
        transitionedCount: allowlistsToTransition.length,
        fromGfcCount: allowlistsFromWeb.length,
        allowlistsLeftCount: documentAllowListsWithMetadata.length -
            allowlistsToTransition.length -
            allowlistsFromWeb.length,
    });
    return true;
}
export async function start() {
    await Prefs.untilLoaded;
    const prefsKeyPopupMigration = "migration_popup_to_smart_allowlist_complete";
    const prefsKeyAllMigration = "migration_all_to_smart_allowlist_complete";
    if (!Prefs.get(prefsKeyPopupMigration)) {
        try {
            const migrated = await migratePopupAllowlistsToSmartAllowlisting();
            await Prefs.set(prefsKeyPopupMigration, migrated);
        }
        catch (error) {
            await Prefs.set(prefsKeyPopupMigration, false);
            reportAndLogError(error, "Failed to migrate allowlists from popup to smart allowlisting");
        }
    }
    if (!Prefs.get(prefsKeyAllMigration)) {
        try {
            const migrated = await migrateAllAllowlistsToSmartAllowlisting();
            await Prefs.set(prefsKeyAllMigration, migrated);
        }
        catch (error) {
            await Prefs.set(prefsKeyAllMigration, false);
            reportAndLogError(error, "Failed to migrate all allowlists to smart allowlisting");
        }
    }
}
